<template>
  <DataTable
    v-model:filters="filterParams"
    :value="filteredTemplates"
    :loading="isLoading"
    filter-display="menu"
    data-key="id_template"
    class="templates-inner-table"
    :paginator="true"
    :rows="pageSize"
    :lazy="false"
    :current-page-report-template="`${t('results')} {first} ${t('to')} {last} ${t('of')} {totalRecords}`"
    :paginator-template="paginatorTemplate"
    @page="onPageChange"
  >
    <template
      v-if="!isElementPartTypeSection"
      #header
    >
      <div
        class="flex justify-content-between flex-wrap mb-2 mt-3"
      >
        <div class="flex align-items-center justify-content-center mt-3">
          <Dropdown
            v-model="groupSelection"
            :options="selection"
            option-value="code"
            option-label="label"
            :placeholder="t('bulkActions.selection.placeholder')"
            @change="selectAllTemplates"
          />
        </div>
      </div>
    </template>
    <template #empty>
      <slot name="empty">
        <p class="text-center">
          {{ t('noRecords') }}
        </p>
      </slot>
    </template>
    <Column header-style="width: 3rem">
      <template #body="templateSlotProps">
        <div class="selection-checkbox">
          <input
            :id="`checkbox-${templateSlotProps.data.id_template}`"
            type="checkbox"
            :checked="localSelectedTemplates.includes(templateSlotProps.data.id_template)"
            class="template-checkbox"
            @change="handleTemplateCheckboxClick(templateSlotProps.data.id_template)"
          >
          <label
            :for="`checkbox-${templateSlotProps.data.id_template}`"
            class="checkbox-label"
          />
        </div>
      </template>
    </Column>
    <Column
      field="label"
      :header="t('templateBuilder.saveSyncElementModal.templateListView.columns.name')"
      sortable
      :filterable="true"
      :show-filter-match-modes="true"
      :header-class="`p-column-header-text ${getFilterStatus('label')}`"
      :filter-match-mode-options="[
        { label: t('filters.contains'), value: 'contains' },
        { label: t('filters.startsWith'), value: 'startsWith' },
        { label: t('filters.endsWith'), value: 'endsWith' },
        { label: t('filters.equals'), value: 'equals' },
      ]"
    >
      <template #filter="{ filterModel }">
        <Filter
          :col="templateColumns[0]"
          :filter-callback="() => {}"
          :filter-model="filterModel"
          :update-value="(value) => { filterModel.value = value; }"
        />
      </template>
      <template #filterclear="{ filterModel, filterCallback }">
        <Button
          :label="t('clear')"
          class="p-button p-button-secondary"
          @click="clearFilter(filterModel, filterCallback, 'label')"
        />
      </template>
      <template #filterapply="{ filterModel, filterCallback }">
        <Button
          :label="t('apply')"
          class="p-button p-button-success"
          @click="applyFilter(filterModel, filterCallback, 'label')"
        />
      </template>
      <template #body="templateSlotProps">
        <div class="template-label-container">
          <i
            :class="previewLoading[templateSlotProps.data.id_template] ? 'fa-regular fa-spinner fa-spin mr-2' : 'fa-regular fa-magnifying-glass mr-2'"
            style="cursor: pointer;"
            @click="handlePreviewWithLoading(templateSlotProps.data)"
          />
          <i
            v-if="templateSlotProps.data.name_marketing_workflow"
            v-tooltip="{ value: t('templateBuilder.saveSyncElementModal.fromAutomatedScenario') }"
            class="far fa-share-nodes template-source-icon mr-2"
          />
          <span class="template-title">
            {{ templateSlotProps.data.label }}
          </span>
        </div>
        <div
          v-if="templateSlotProps.data.name_marketing_workflow"
          class="template-scenario-subtitle"
        >
          {{ templateSlotProps.data.name_marketing_workflow }}
        </div>
        <!-- Section selection indicator -->
        <div
          v-if="isElementPartTypeSection && localSelectedTemplates.includes(templateSlotProps.data.id_template)"
          class="selected-sections-indicator"
        >
          <i class="far fa-layer-group mr-1" />
          <span class="sections-count">
            {{ getSelectedSectionsForTemplate(templateSlotProps.data.id_template).length }} {{ t('templateBuilder.saveSyncElementModal.sectionsSelected', { count: getSelectedSectionsForTemplate(templateSlotProps.data.id_template).length }) }}
          </span>
        </div>
      </template>
    </Column>
    <Column
      field="date_modification"
      :header="t('campaigns.bulk.list.columns.last_update')"
      sortable
      :filterable="true"
      :show-filter-match-modes="false"
      :header-class="`p-column-header-date ${getFilterStatus('date_modification')}`"
      :filter-match-mode-options="[
        { label: t('filters.interval'), value: 'interval' },
      ]"
    >
      <template #filter="{ filterModel }">
        <Filter
          :col="templateColumns[1]"
          :filter-callback="() => {}"
          :filter-model="filterModel"
          :update-value="(value) => { filterModel.value = value; }"
        />
      </template>
      <template #filterclear="{ filterModel, filterCallback }">
        <Button
          :label="t('clear')"
          class="p-button p-button-secondary"
          @click="clearFilter(filterModel, filterCallback, 'date_modification')"
        />
      </template>
      <template #filterapply="{ filterModel, filterCallback }">
        <Button
          :label="t('apply')"
          class="p-button p-button-success"
          @click="applyFilter(filterModel, filterCallback, 'date_modification')"
        />
      </template>
      <template #body="templateSlotProps">
        {{ templateSlotProps.data.date_modification }}
      </template>
    </Column>
  </DataTable>
</template>

<script lang="ts">
import {
  defineComponent,
  ref,
  computed,
  PropType,
  watch,
  nextTick,
} from 'vue';
import { useI18n } from 'vue-i18n';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Button from 'primevue/button';
import Dropdown from 'primevue/dropdown';
import Tooltip from 'primevue/tooltip';
import { TemplatesCustom } from '@/types/generated-types/graphql';
import { TemplateStructureEnum } from '@/types';
import Filter from '@/components/table/Filter.vue';
import { isDateInRange } from '@/helpers/Date';

// Extended interface to include name_marketing_workflow and date_creation from GraphQL response
interface ExtendedTemplatesCustom extends TemplatesCustom {
  name_marketing_workflow?: string;
  date_creation?: string;
  date_modification?: string;
}

export default defineComponent({
  name: 'TemplateDataTable',

  components: {
    DataTable,
    Column,
    Button,
    Dropdown,
    Filter,
  },

  directives: {
    tooltip: Tooltip,
  },

  props: {
    templates: {
      type: Array as PropType<ExtendedTemplatesCustom[]>,
      required: true,
    },

    isLoading: {
      type: Boolean,
      default: false,
    },

    elementPartTypeOrSubtype: {
      type: String,
      required: true,
    },

    selectedTemplates: {
      type: Set as PropType<Set<number>>,
      required: true,
    },

    selectedSectionsByTemplate: {
      type: Map as PropType<Map<number, string[]>>,
      required: true,
    },
  },

  emits: ['preview-template', 'selection-changed', 'open-section-modal'],

  setup(props, { emit }) {
    const { t } = useI18n();
    const pageSize = ref(10);
    const currentPage = ref(0);
    const paginatorTemplate = ref('FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport');
    const localSelectedTemplates = ref<number[]>(Array.from(props.selectedTemplates));
    const lastEmittedSelection = ref<number[]>(Array.from(props.selectedTemplates));
    const previewLoading = ref<Record<number, boolean>>({});

    // Selection system
    const groupSelection = ref<string | null>(null);
    const selection = [
      {
        code: 'unselect',
        label: t('bulkActions.selection.unselect'),
      },
      {
        code: 'visible',
        label: t('bulkActions.selection.visible'),
      },
      {
        code: 'all',
        label: t('bulkActions.selection.all'),
      },
    ];

    // Filtered templates storage (reactive)
    const filteredTemplates = ref<ExtendedTemplatesCustom[]>([]);

    // Check if element part type is a section
    const isElementPartTypeSection = computed(() => props.elementPartTypeOrSubtype === TemplateStructureEnum.SECTION);

    // Watch for changes in selectedTemplates prop to sync with local state
    watch(() => props.selectedTemplates, (newSelection) => {
      localSelectedTemplates.value = Array.from(newSelection);
      lastEmittedSelection.value = Array.from(newSelection);
    }, { deep: true });

    const onTemplateSelectionChanged = () => {
      // Avoid emitting if selection hasn't actually changed
      const currentSelection = [...localSelectedTemplates.value].sort();
      const lastSelection = [...lastEmittedSelection.value].sort();

      if (JSON.stringify(currentSelection) === JSON.stringify(lastSelection)) {
        return;
      }

      lastEmittedSelection.value = [...localSelectedTemplates.value];

      emit('selection-changed', {
        selectedIds: localSelectedTemplates.value,
        selectedSectionsByTemplate: Object.fromEntries(props.selectedSectionsByTemplate),
      });
    };

    // Selection functions inspired by SpmTable.vue
    const selectAllTemplates = async () => {
      if (groupSelection.value === 'unselect') {
        localSelectedTemplates.value = [];
      } else if (groupSelection.value === 'all') {
        // Select all filtered templates
        localSelectedTemplates.value = filteredTemplates.value.map((item) => item.id_template);
      } else if (groupSelection.value === 'visible') {
        // Select only templates visible on current page
        // Use nextTick to ensure currentPage is updated
        await nextTick();
        const startIndex = currentPage.value * pageSize.value;
        const endIndex = Math.min(startIndex + pageSize.value, filteredTemplates.value.length);
        const visibleTemplates = filteredTemplates.value.slice(startIndex, endIndex);
        const newSelections = visibleTemplates.map((item) => item.id_template);
        const existingSelections = localSelectedTemplates.value.filter((id) => !newSelections.includes(id));
        localSelectedTemplates.value = [...existingSelections, ...newSelections];
      }

      // Emit the change
      nextTick(() => {
        onTemplateSelectionChanged();
      });
    };

    // Clean selection to keep only templates present in filtered results
    const cleanSelection = () => {
      const filteredTemplateIds = filteredTemplates.value.map((item) => item.id_template);
      const cleanedSelection = localSelectedTemplates.value.filter((id) => filteredTemplateIds.includes(id));

      // Only update if the selection actually changed
      if (cleanedSelection.length !== localSelectedTemplates.value.length) {
        localSelectedTemplates.value = cleanedSelection;
        groupSelection.value = null;
        nextTick(() => {
          onTemplateSelectionChanged();
        });
      }
    };

    // Find template by ID
    const findTemplateById = (templateId: number): ExtendedTemplatesCustom | undefined => props.templates.find((item) => item.id_template === templateId);

    // Get selected sections for a template
    const getSelectedSectionsForTemplate = (templateId: number): string[] => props.selectedSectionsByTemplate.get(templateId) || [];

    const onPageChange = async (event: any) => {
      currentPage.value = event.page;
    };

    const handleTemplateCheckboxClick = (templateId: number) => {
      // If element part type is a section, open modal
      if (isElementPartTypeSection.value) {
        // Reset the checkbox state since we don't want it to change
        nextTick(() => {
          const checkbox = document.getElementById(`checkbox-${templateId}`) as HTMLInputElement;
          if (checkbox) {
            checkbox.checked = localSelectedTemplates.value.includes(templateId);
          }
        });

        const template = findTemplateById(templateId);
        if (template) {
          emit('open-section-modal', template);
        }
        return;
      }

      // For non-section elements, update selection based on checkbox state
      const checkbox = document.getElementById(`checkbox-${templateId}`) as HTMLInputElement;
      if (checkbox) {
        if (checkbox.checked) {
          // Add to selection
          if (!localSelectedTemplates.value.includes(templateId)) {
            localSelectedTemplates.value = [...localSelectedTemplates.value, templateId];
          }
        } else {
          // Remove from selection
          localSelectedTemplates.value = localSelectedTemplates.value.filter((id) => id !== templateId);
        }
      }

      // Emit the change after nextTick
      nextTick(() => {
        onTemplateSelectionChanged();
      });
    };

    // Configuration des colonnes avec filtres
    const templateColumns = [
      {
        field: 'label',
        header: t('templateBuilder.saveSyncElementModal.templateListView.columns.name'),
        type: 'text',
        filterable: true,
        sortable: true,
        editable: false,
        style: 'width: 60%',
        matchModes: [
          { label: t('filters.contains'), value: 'contains' },
          { label: t('filters.startsWith'), value: 'startsWith' },
          { label: t('filters.endsWith'), value: 'endsWith' },
          { label: t('filters.equals'), value: 'equals' },
        ],
      },
      {
        field: 'date_modification',
        header: t('campaigns.bulk.list.columns.last_update'),
        type: 'date',
        filterable: true,
        sortable: true,
        editable: false,
        style: 'width: 40%',
        filterSettings: { type: 'date' },
        matchModes: [
          { label: t('filters.interval'), value: 'interval' },
        ],
      },
    ];

    // Active filters for visual indication
    const activeFilters = ref<Set<string>>(new Set());

    // Initialize filter params for PrimeVue
    const filterParams = ref<Record<string, any>>(templateColumns
      .filter((col) => col.filterable)
      .reduce<object>((acc, col) => {
        let matchMode = 'contains';
        if (col.filterSettings?.type === 'date') {
          matchMode = 'interval';
        }
        return ({
          ...acc,
          [col.field]: {
            operator: null,
            constraints: [{ value: '', matchMode }],
          },
        });
      }, {}));

    // Filter function like SpmTable.vue filterClientSideTableData
    const filterClientSideTemplateData = () => {
      filteredTemplates.value = props.templates.filter((template) => Object.entries(filterParams.value).every(([column, filter]) => {
        const { matchMode: filterMatchMode, value: filterConstraintValue } = filter.constraints[0];
        const columnValue = String(template[column as keyof typeof template] || '');

        if (
          filterConstraintValue === ''
            || (Array.isArray(filterConstraintValue) && filterConstraintValue.length === 0)
        ) return true;

        const filterValueStr = String(filterConstraintValue).toLowerCase();
        const columnValueStr = columnValue.toLowerCase();

        if (filterMatchMode === 'startsWith') {
          return columnValueStr.startsWith(filterValueStr);
        }

        if (filterMatchMode === 'endsWith') {
          return columnValueStr.endsWith(filterValueStr);
        }

        if (filterMatchMode === 'equals') {
          return columnValueStr === filterValueStr;
        }

        if (filterMatchMode === 'interval' && column === 'date_modification') {
          // Handle date filtering for interval mode
          return isDateInRange(columnValue, filterConstraintValue);
        }

        // Default to 'contains'
        return columnValueStr.includes(filterValueStr);
      }));
    };

    // Get filter status for visual indication
    const getFilterStatus = (field: string) => (activeFilters.value.has(field) ? 'column-filter-active' : '');

    // Clear filter
    const clearFilter = (filterModel: any, filterCallback: Function, field: string) => {
      // Clear the filterModel constraints directly like in SpmTable.vue
      if (filterModel.constraints && filterModel.constraints[0]) {
        // eslint-disable-next-line no-param-reassign
        filterModel.constraints[0].value = '';
        // eslint-disable-next-line no-param-reassign
        filterModel.constraints[0].matchMode = field === 'date_modification' ? 'interval' : 'contains';
      }

      // Force reactivity by creating new object
      const newFilterParams = { ...filterParams.value };
      if (newFilterParams[field]) {
        newFilterParams[field] = {
          ...newFilterParams[field],
          constraints: [{
            value: '',
            matchMode: field === 'date_modification' ? 'interval' : 'contains',
          }],
        };
      }
      filterParams.value = newFilterParams;

      // Apply client-side filtering like SpmTable.vue
      filterClientSideTemplateData();

      // Clean selection to keep only templates present in filtered results
      cleanSelection();

      activeFilters.value.delete(field);
      currentPage.value = 0;
      filterCallback();
    };

    // Apply filter
    const applyFilter = (filterModel: any, filterCallback: Function, field: string) => {
      // Read from filterModel constraints like in SpmTable.vue
      const filterConstraintValue = filterModel.constraints[0].value;
      const filterMatchMode = filterModel.constraints[0].matchMode;

      // Force reactivity by creating new object
      const newFilterParams = { ...filterParams.value };
      if (newFilterParams[field]) {
        newFilterParams[field] = {
          ...newFilterParams[field],
          constraints: [{
            value: filterConstraintValue,
            matchMode: filterMatchMode,
          }],
        };
      }
      filterParams.value = newFilterParams;

      // Apply client-side filtering like SpmTable.vue
      filterClientSideTemplateData();

      // Clean selection to keep only templates present in filtered results
      cleanSelection();

      // Manage visual indication
      if (filterConstraintValue && (typeof filterConstraintValue === 'object' || String(filterConstraintValue).trim() !== '')) {
        activeFilters.value.add(field);
      } else {
        activeFilters.value.delete(field);
      }

      currentPage.value = 0;
      filterCallback();
    };

    // Handle preview with loading state like in MyTemplates.vue
    const handlePreviewWithLoading = (templateData: ExtendedTemplatesCustom) => {
      previewLoading.value[templateData.id_template] = true;

      emit('preview-template', {
        id: templateData.id_template,
        templateType: templateData.type,
        templateLabel: templateData.label,
      }, () => {
        previewLoading.value[templateData.id_template] = false;
      });
    };

    // Initialize filtered templates with all data when props change
    watch(() => props.templates, (newTemplates) => {
      filteredTemplates.value = [...newTemplates];
    }, { immediate: true });

    return {
      t,
      filteredTemplates,
      previewLoading,
      isElementPartTypeSection,
      filterParams,
      templateColumns,
      localSelectedTemplates,
      pageSize,
      paginatorTemplate,
      groupSelection,
      selection,
      selectAllTemplates,
      handleTemplateCheckboxClick,
      handlePreviewWithLoading,
      getSelectedSectionsForTemplate,
      clearFilter,
      applyFilter,
      getFilterStatus,
      onPageChange,
    };
  },
});
</script>

<style scoped lang="scss">
.templates-inner-table {
  .template-label-container {
    display: flex;
    align-items: center;

    .template-source-icon {
      color: #6c757d;
      font-size: 1rem;
    }

    .template-title {
      font-weight: 500;
      color: #2c3e50;
    }
  }

  .template-scenario-subtitle {
    font-size: 0.8rem;
    color: #6c757d;
    font-style: italic;
    margin-top: 0.25rem;
    margin-left: 1.5rem; // Align with title (icon + margin)
  }

  .selection-checkbox {
    display: flex;
    align-items: center;
    justify-content: center;

    .template-checkbox {
      display: none;
    }

    .checkbox-label {
      display: block;
      width: 18px;
      height: 18px;
      background-color: rgba(255, 255, 255, 0.9);
      border: 2px solid #ddd;
      border-radius: 4px;
      cursor: pointer;
      transition: all 0.3s ease;
      position: relative;

      &:hover {
        border-color: #94c840;
        background-color: rgba(148, 200, 64, 0.1);
      }

      &::after {
        content: '';
        position: absolute;
        top: 1px;
        left: 5px;
        width: 4px;
        height: 8px;
        border: solid white;
        border-width: 0 2px 2px 0;
        transform: rotate(45deg);
        opacity: 0;
        transition: opacity 0.3s ease;
      }
    }

    .template-checkbox:checked + .checkbox-label {
      background-color: #94c840;
      border-color: #94c840;

      &::after {
        opacity: 1;
      }
    }
  }

  .selected-sections-indicator {
    display: flex;
    align-items: center;
    margin-top: 0.25rem;
    margin-left: 1.5rem; // Align with title (icon + margin)
    font-size: 0.8rem;
    color: #94c840;

    .sections-count {
      font-weight: 500;
    }

    i {
      font-size: 0.75rem;
    }
  }
}

// Indication visuelle des filtres actifs
.column-filter-active .p-column-filter-menu-button {
  background: #ecfdf5 !important;
  color: #047857 !important;
}
</style>

<style lang="scss">
.templates-inner-table {
  .p-datatable-header {
    background-color: initial !important;
    padding: 0 !important;
    border: none !important;
  }

  :deep() .p-paginator {
    padding: 0.5rem 0;
    font-size: 0.9rem;

    & .p-paginator-current {
      margin-left: auto;
    }

    & .p-paginator-pages {
      & .p-link {
        font-size: 0.9rem !important;
      }
    }

    & .p-dropdown {
      margin-right: 0;

      & .p-dropdown-label {
        font-size: 0.9rem;
      }
    }

    @media screen and (max-width: $sm-max) {
      display: block;
      text-align: center;
      position: relative;
      .p-paginator-current {
        display: block;
        text-align: left;
        line-height: 2rem;
        margin-bottom: 1rem;
      }

      .p-dropdown {
        position: absolute;
        right: 2rem;
        top: .5rem;
      }
    }
  }
}
</style>
